<script setup lang="ts">
import { h } from 'vue'
import { PortableText, type PortableTextComponent } from '@portabletext/vue'
import { NuxtLink } from '#components'
import BasicImage from '../common/BasicImage.vue'
import MuxVideoWrapper from '../common/MuxVideoWrapper.vue'

interface InternalLinkValue {
  slug?: {
    current?: string
  }
}

interface ExternalLinkValue {
  url: string
  title?: string
}

interface ImageValue {
  asset?: {
    _ref: string
  }
  alt?: string
}

interface MediaValue {
  asset?: {
    playbackId: string
  }
}

type Props = {
  classes?: string
  content: unknown // PortableText content structure is dynamic
}

const _props = defineProps<Props>()

// Image component
const image: PortableTextComponent<ImageValue> = ({ value }: { value: ImageValue }) => {
  return h(BasicImage, { image: value })
}

const video: PortableTextComponent<MediaValue> = ({ value }: { value: MediaValue }) => {
  if (!value.asset) {
    // Handle the case where asset is undefined
    return null // Or return a placeholder component
  }

  const attr = {
    loop: false,
    muted: false,
    autoplay: false,
    controls: true,
  }

  return h('div', { class: 'my-8' }, [h(MuxVideoWrapper, { video: value, attr })])
}

const blockquote: PortableTextComponent<unknown> = ({ value }: { value: any }) =>
  h(
    'blockquote',
    {
      class:
        'text-lg lg:text-xl relative pl-12 leading-tight italic my-10 before:content-["“"] before:text-4xl before:absolute before:-left-6 before:-top-4 before:pl-6 max-w-[50ch] text-balance',
    },
    [
      h('p', value.text),
      h('footer', { class: 'text-sm mt-2 not-italic' }, [
        h('cite', { class: 'not-italic' }, value.author), // Make author bold with Tailwind CSS
        h('span', {}, ` - ${value.authorTitle}`),
      ]),
    ],
  )

const serializers = {
  block: {
    normal: (_: any, { slots }: any) =>
      h('p', { class: 'text-base leading-[1.3] mb-4 last:mb-0' }, slots.default?.()),
    h2: (_: any, { slots }: any) =>
      h('h2', { class: 'mb-2 mt-8  text-lg font-bold' }, slots.default?.()),
    h3: (_: any, { slots }: any) =>
      h('h3', { class: ' text-base font-bold mb-2 mt-4' }, slots.default?.()),
    blockquote: (_: any, { slots }: any) =>
      h('blockquote', { class: 'text-lg leading-tight italic my-10 ' }, slots.default?.()),
  },
  list: {
    bullet: (_: any, { slots }: any) =>
      h(
        'ul',
        { class: 'pl-8 font-light text-base flex flex-col gap-6 list-disc my-10' },
        slots.default?.(),
      ),
    number: (_: any, { slots }: any) =>
      h(
        'ol',
        { class: 'pl-8 font-light text-base flex flex-col gap-6 my-10 list-decimal' },
        slots.default?.(),
      ),
  },
  listItem: {
    bullet: (_: any, { slots }: any) => h('li', { class: 'pl-base' }, slots.default?.()),
    number: (_: any, { slots }: any) => h('li', { class: 'pl-base' }, slots.default?.()),
    checkmarks: (_: any, { slots }: any) => h('li', ['✅', slots.default?.()]),
  },
  marks: {
    strong: (_: any, { slots }: any) => h('strong', { class: 'font-bold' }, slots.default?.()),
    em: (_: any, { slots }: any) => h('em', { class: 'italic' }, slots.default?.()),
    internalLink: ({ value }: { value: InternalLinkValue }, { slots }: any) => {
      return h(
        NuxtLink,
        {
          to: value.slug?.current ?? '#',
          class: 'underline',
        },
        () => slots.default?.(),
      )
    },
    externalLink: ({ value }: { value: ExternalLinkValue }, { slots }: any) => {
      const title = value.title || value.url
      return h(
        'a',
        {
          class: 'underline',
          href: value.url,
          target: '_blank',
          title: title,
          rel: 'noopener noreferrer nofollow',
        },
        slots.default?.(),
      )
    },
  },
  types: {
    mainImage: image,
    video: video,
    blockquote: blockquote,
  },
}
</script>

<template>
  <PortableText v-if="content" :value="content" :components="serializers" :class="classes" />
</template>
